import { useEffect, useMemo, useState } from 'react';
import { Sun, Camera, MapPin, ChevronRight } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { BlockingDiagram, BlockingLegend } from '@/components/scout/BlockingDiagram';
import { adminScoutSunNow, sharedShootSunNow } from '@/lib/api';
import { SHOOT_POSITIONS } from '@/constants/testIds';
import { readBoolPref, writeBoolPref } from '@/lib/prefs';

const LIVE_KEY = 'lxra.shoot.liveSun';

// "5:40 PM" -> minutes since midnight (local to the location); null if unparseable
const clockToMinutes = (clock) => {
  if (!clock) return null;
  const m = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i.exec(clock.trim());
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2] || '0', 10);
  const ap = (m[3] || '').toLowerCase();
  if (ap === 'pm' && h < 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  return h * 60 + min;
};

/**
 * Shoot Mode → Positions: the Simulator's moments as swipeable diagrams for the phone.
 * Live sun is ON by default here (you're on location). Works for the admin view and the
 * shared crew link (key-free live-sun endpoint).
 */
export const ShootPositions = ({ scout, mode, shootKey }) => {
  const positions = useMemo(() => scout?.positions || [], [scout?.positions]);
  const [liveOn, setLiveOn] = useState(() => readBoolPref(LIVE_KEY, true));
  const [live, setLive] = useState(null);
  const [liveError, setLiveError] = useState(null);

  useEffect(() => { writeBoolPref(LIVE_KEY, liveOn); }, [liveOn]);

  useEffect(() => {
    if (!liveOn || !scout?.id) { setLive(null); return undefined; }
    let cancelled = false;
    const fetchLive = () => (mode === 'admin' ? adminScoutSunNow(scout.id) : sharedShootSunNow(shootKey))
      .then((d) => { if (!cancelled) { setLive(d); setLiveError(null); } })
      .catch(() => { if (!cancelled) setLiveError('Live sun unavailable right now.'); });
    fetchLive();
    const t = setInterval(fetchLive, 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [liveOn, scout?.id, mode, shootKey]);

  // Which moment is "now"? Nearest planned time on the shoot day, else the first upcoming.
  const nowIdx = useMemo(() => {
    if (!live?.clock || !positions.length) return -1;
    const nowMin = clockToMinutes(live.clock);
    if (nowMin == null) return -1;
    let best = -1;
    let bestDelta = Infinity;
    positions.forEach((p, i) => {
      const m = clockToMinutes(p.time);
      if (m == null) return;
      const d = Math.abs(m - nowMin);
      if (d < bestDelta) { bestDelta = d; best = i; }
    });
    return bestDelta <= 90 ? best : -1; // only highlight when within 90 minutes
  }, [live?.clock, positions]);

  useEffect(() => {
    if (nowIdx >= 0) document.querySelector(`[data-testid="${SHOOT_POSITIONS.card(nowIdx)}"]`)?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [nowIdx]);

  if (!scout) return null;

  return (
    <section data-testid={SHOOT_POSITIONS.section} className="rounded-2xl border border-[hsl(var(--lxra-gold))]/40 bg-[#0F0F10] overflow-hidden">
      <div className="flex items-start justify-between gap-3 p-3.5 pb-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-gold))] flex items-center gap-1.5"><MapPin className="h-3 w-3" /> Positions · {scout.place_name}</p>
          <p className="text-[11px] text-[hsl(var(--lxra-muted))] mt-0.5">Swipe through the moments. Gold camera is you, white dots are your subjects.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))] shrink-0">
          <Sun className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Live
          <Switch checked={liveOn} onCheckedChange={setLiveOn} data-testid={SHOOT_POSITIONS.liveSwitch} className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]" />
        </label>
      </div>

      {liveOn && (
        <div data-testid={SHOOT_POSITIONS.liveStrip} className="mx-3.5 mb-2 rounded-xl border border-[hsl(var(--lxra-gold))]/30 bg-[hsl(var(--lxra-gold))]/[0.06] px-3 py-2 text-[11px]">
          {liveError ? (
            <span className="text-amber-300">{liveError}</span>
          ) : !live ? (
            <span className="text-[hsl(var(--lxra-muted))]">Reading the sun…</span>
          ) : (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-display text-base leading-none text-[hsl(var(--lxra-fg))]">{live.clock}</span>
              <span className={live.sun.elevation < -0.8 ? 'text-sky-300' : 'text-[hsl(var(--lxra-gold))]'}>
                {live.sun.elevation < -0.8 ? `Sun below horizon (${live.sun.direction})` : `Sun ${live.sun.direction} · ${live.sun.elevation}°`} · {live.sun.phase}
              </span>
              {live.current_window && <span className="text-emerald-300">{live.current_window.label} now — {live.current_window.minutes_left} min left</span>}
              {!live.current_window && live.next_window && <span className="text-[hsl(var(--lxra-muted))]">{live.next_window.label} at {live.next_window.start_clock} · in {Math.floor(live.next_window.minutes_until / 60) ? `${Math.floor(live.next_window.minutes_until / 60)}h ` : ''}{live.next_window.minutes_until % 60}m</span>}
              {!live.is_shoot_day && <span className="text-[hsl(var(--lxra-muted))]">(not the shoot date)</span>}
            </div>
          )}
        </div>
      )}

      {positions.length === 0 ? (
        <p data-testid={SHOOT_POSITIONS.empty} className="px-3.5 pb-4 text-xs text-[hsl(var(--lxra-muted))]">No moments plotted yet — run the Shoot Simulator on the Scout brief and they will show up here.</p>
      ) : (
        <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-3.5 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {positions.map((p, i) => {
            const isNow = i === nowIdx;
            return (
              <article
                key={p.idx ?? i}
                data-testid={SHOOT_POSITIONS.card(i)}
                className={`snap-center shrink-0 w-[82vw] max-w-[340px] rounded-2xl border p-3 ${isNow ? 'border-[hsl(var(--lxra-gold))] bg-[hsl(var(--lxra-gold))]/[0.06]' : 'border-[hsl(var(--lxra-border))] bg-[#0B0B0C]'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-gold))]">Moment {i + 1} · {p.time}</p>
                    <h4 className="font-display text-base leading-tight text-[hsl(var(--lxra-fg))] mt-0.5 truncate">{p.title}</h4>
                    {p.spot && <p className="text-[10px] text-[hsl(var(--lxra-muted))] truncate">{p.spot}</p>}
                  </div>
                  {isNow && <span data-testid={SHOOT_POSITIONS.nowBadge(i)} className="shrink-0 rounded-full bg-[hsl(var(--lxra-gold))] px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-[#0A0A0A]">Now</span>}
                </div>
                <div className="mt-2 rounded-xl bg-[#0B0B0C] p-1">
                  <BlockingDiagram
                    blocking={p.blocking}
                    fovDeg={p.photo?.fov_deg || 40}
                    testId={SHOOT_POSITIONS.diagram(i)}
                    sunTrack={scout.sun_track || []}
                    site={scout.site}
                    liveSun={liveOn && live ? live.sun : null}
                    markers={scout.markers || []}
                    compact
                  />
                  <BlockingLegend blocking={p.blocking} fovDeg={p.photo?.fov_deg || 40} />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[hsl(var(--lxra-muted))]">
                  {p.photo?.lens && <span className="inline-flex items-center gap-1 text-[hsl(var(--lxra-fg))]"><Camera className="h-3 w-3" /> {p.photo.lens}</span>}
                  {p.photo?.aperture && <span>{p.photo.aperture} · {p.photo.shutter} · ISO {p.photo.iso}</span>}
                  {p.light_intent && <span className="text-[hsl(var(--lxra-gold))]">{p.light_intent}</span>}
                </div>
                {p.blocking?.notes && <p className="mt-1.5 text-[11px] text-[hsl(var(--lxra-muted))] line-clamp-3">{p.blocking.notes}</p>}
                {p.video?.movement && <p className="mt-1 text-[10px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))]">Video: {p.video.shot_type} · {p.video.movement}</p>}
              </article>
            );
          })}
          <div className="shrink-0 w-2" aria-hidden="true"><ChevronRight className="sr-only" /></div>
        </div>
      )}
    </section>
  );
};
